import { defineStore } from "pinia";
import { ref } from "vue";
import type {
	Basket,
	BasketItem,
	LookupBaskets
} from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { PaymentMethod } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/paymentMethods";

interface State {
	cartId: number | undefined;
	basket: Ref<Basket | undefined>;
	updated: number;
	updating: boolean;
	paymentMethod: PaymentMethod | null | { error: string };
}

export const useCartStore = defineStore({
	id: "cartStore",
	state: (): State => ({
		cartId: 0,
		basket: ref(),
		updated: 0,
		updating: false,
		paymentMethod: null
	}),
	actions: {
		setCartId(id: number | undefined) {
			this.cartId = id;
		},
		setBasket(basket: Basket | undefined) {
			this.basket = basket;
		},
		async initCart() {
			if (this.cartId && useNorceStore().storeContext.cultureCode) {
				let basket;
				basket = await useNorcePwa().getBasket({
					id: +this.cartId,
					...useNorceStore().storeContext
				});
				await this.updatePayment(basket);
				if (!basket?.PaymentMethodId) {
					basket = await useNorcePwa().getBasket({
						id: +this.cartId,
						...useNorceStore().storeContext
					});
				}
				this.basket = basket;
			}
		},
		async initCartUser() {
			// check for basket by user and with status Active
			const basketResult: LookupBaskets = await useNorcePwa().lookupBasket({
				$filter:
					"BuyerCustomerId eq " +
					userStore()?.getUser?.Id +
					" and StatusId eq 3"
			});
			let basketFound;

			if (this.cartId) {
				basketFound = basketResult.value.filter(
					({ Id }) => Id === +this.cartId!
				)[0];
			}
			if (!basketFound) {
				basketFound = basketResult.value[0];
			}
			if (basketResult.value.length > 0) {
				const basketId = basketFound?.Id;
				const userBasket: Basket = await useNorcePwa().getBasket({
					id: basketId,
					...useNorceStore().storeContext
				});
				await this.updateBasketFromGuest(userBasket);

				this.basket = userBasket;
				this.cartId = basketId;
			} else {
				if (this.cartId) {
					const basketResult = await useNorcePwa().lookupBasket({
						$filter: "StatusId eq 3"
					});
					const basketCreatedAnonymous = basketResult.value.filter(
						({ Id }) => Id === this.cartId
					)[0];
					if (basketCreatedAnonymous) {
						const user = userStore().getUser;
						if (user) {
							await useNorcePwa().updateBuyer(
								{
									basketId: this.cartId,
									updatedBy: user.Id!
								},
								{ ...user }
							);
						}

						this.basket = await useNorcePwa().getBasket({
							id: this.cartId,
							...useNorceStore().storeContext
						});
					} else {
						await this.createNewBasket();
					}
				} else {
					// create new basket for user
					await this.createNewBasket();
				}
			}
		},
		itemsCount() {
			const items = this.basket?.Items?.filter(
				(item) => item.ManufacturerName !== "Freight"
			);
			return items?.reduce((acc, item) => acc + item.Quantity, 0) ?? 0;
		},
		async addToCart(item: BasketItem) {
			this.updating = true;
			const userId = userStore()?.getUser?.Id;

			if (!this.cartId) {
				const basket = await useNorcePwa().createBasket({
					createdBy: userId?.toString() || "0",
					...(useProductStore()?.priceLists?.[0]?.Id
						? {
								pricelistSeed: useProductStore()?.priceLists?.[0]?.Id.toString()
							}
						: {})
				});

				this.cartId = basket.Id;
				this.basket = await useNorcePwa().addToBasket(
					{
						basketId: basket.Id,
						createdBy: userId?.toString() || "0"
					},
					item
				);
				this.updating = false;
			} else {
				// check if item is already in cart
				const itemInCart = this?.basket?.Items?.find(
					(i: BasketItem) => i.PartNo === item.PartNo
				);

				if (itemInCart) {
					const basketItem = {
						LineNo: itemInCart.LineNo,
						PartNo: item.PartNo,
						Quantity: itemInCart.Quantity + item.Quantity
					};

					this.basket = await useNorcePwa().updateBasketItem(
						{
							basketId: this.cartId
						},
						basketItem
					);
				} else {
					this.basket = await useNorcePwa().addToBasket(
						{
							basketId: this.cartId,
							createdBy: userId?.toString() || "0"
						},
						item
					);
				}
				this.updating = false;
			}
			useNuxtApp().$toast.success(item.Name + " was added to cart");
		},
		async addItemsToCart(items: BasketItem[]) {
			this.updating = true;
			const userId = userStore()?.getUser?.Id;

			if (!this.cartId) {
				const basket = await useNorcePwa().createBasket({
					createdBy: userId?.toString() || "0",
					...(useProductStore()?.priceLists?.[0]?.Id
						? {
								pricelistSeed: useProductStore()?.priceLists?.[0]?.Id.toString()
							}
						: {})
				});

				this.cartId = basket.Id;
			}

			const itemsInBasket: BasketItem[] = [];
			const itemsNotInBasket: BasketItem[] = [];

			items.forEach((item) => {
				const itemInBasket = this.basket?.Items.find(
					(i: BasketItem) => i.PartNo === item.PartNo
				);

				if (itemInBasket) {
					itemsInBasket.push({
						...item,
						Quantity: itemInBasket.Quantity + item.Quantity,
						LineNo: itemInBasket.LineNo
					});
				} else {
					itemsNotInBasket.push(item);
				}
			});

			if (itemsInBasket.length > 0) {
				this.basket = await useNorcePwa().updateBasketItems(
					{ basketId: this.cartId },
					itemsInBasket
				);
			}
			if (itemsNotInBasket.length > 0) {
				this.basket = await useNorcePwa().addItemsToBasket(
					{
						basketId: this.cartId,
						createdBy: userId?.toString() || "0"
					},
					itemsNotInBasket
				);
			}

			this.updating = false;

			useNuxtApp().$toast.success("Items were added to cart");
		},
		async removeFromCart(lineno: number) {
			this.updating = true;
			const userId = userStore()?.getUser?.Id;
			useHelpersStore().setIsLoading(true);
			this.basket = await useNorcePwa().deleteBasketItem({
				basketId: this.cartId,
				lineNo: lineno,
				createdBy: userId?.toString() || "0"
			});
			this.updating = false;
			useHelpersStore().setIsLoading(false);
		},
		async updateCartItem(lineno: number, partno: string, quantity: number) {
			this.updating = true;
			useHelpersStore().setIsLoading(true);
			const basketItem = {
				LineNo: lineno,
				PartNo: partno,
				Quantity: quantity
			};

			this.basket = await useNorcePwa().updateBasketItem(
				{
					basketId: this.cartId
				},
				basketItem
			);
			this.updating = false;
			useHelpersStore().setIsLoading(false);
		},
		async updateBasketFromGuest(userBasket: Basket) {
			const userId = userStore()?.getUser?.Id;
			if (this.cartId !== userBasket.Id) {
				const cartIdBeforeMerge = this.cartId;
				const guestBasket = await useNorcePwa().getBasket({
					id: this.cartId,
					...useNorceStore().storeContext
				});

				if (guestBasket && guestBasket?.Items?.length) {
					const itemsCombined = combineQuantityOfBasketItems(
						guestBasket.Items,
						userBasket.Items,
						"ProductId"
					);

					const itemsInUserBasket = itemsCombined.filter((userItem) =>
						userBasket?.Items?.map(({ ProductId }) => ProductId).includes(
							userItem.ProductId
						)
					);
					const itemsInGuestBasket = itemsCombined.filter(
						(userItem) =>
							!userBasket?.Items?.map(({ ProductId }) => ProductId).includes(
								userItem.ProductId
							)
					);

					if (itemsInUserBasket) {
						await useNorcePwa().updateBasketItems(
							{ basketId: userBasket.Id },
							itemsInUserBasket
						);
					}
					await useNorcePwa().addItemsToBasket(
						{
							basketId: userBasket.Id,
							createdBy: userId?.toString() || "0"
						},
						itemsInGuestBasket
					);
					if (cartIdBeforeMerge) {
						return await useNorcePwa().deleteBasket({
							basketId: this.cartId,
							updatedBy: userId?.toString() || "0"
						});
					}
				}
			}
			return true;
		},
		async createNewBasket() {
			const userId = userStore()?.getUser?.Id;

			// create new basket for user
			const basketItem = {
				...(userId && { CustomerId: userId }),
				Items: this.basket?.Items ?? []
			};
			let basket = await useNorcePwa().createBasket(
				{
					createdBy: userId?.toString() ?? "0",
					...(useProductStore()?.priceLists?.[0]?.Id
						? {
								pricelistSeed: useProductStore()?.priceLists?.[0]?.Id.toString()
							}
						: {})
				},
				basketItem
			);
			// set cartId immediately
			this.cartId = basket.Id;
			await this.updatePayment(basket);

			if (!basket?.PaymentMethodId) {
				basket = await useNorcePwa().getBasket({
					id: basket.Id,
					...useNorceStore().storeContext
				});
			}
			this.basket = basket;
		},
		async updatePayment(basket: Basket) {
			if (!basket?.PaymentMethodId) {
				if (!this.paymentMethod) {
					this.paymentMethod = await useNorcePwa().getPaymentMethod({
						currencyId: useNorceStore().getCurrentCurrency?.Id,
						code: "norcecheckout"
					});

					if (this.paymentMethod) {
						const paymentMethod: PaymentMethod = JSON.parse(
							JSON.stringify(this.paymentMethod)
						);
						await useNorcePwa().updateBasketPaymentMethod({
							basketId: basket.Id!,
							paymentMethodId: paymentMethod.PaymentMethodId
						});
					}
				}
			}
		}
	},
	persist: [{ storage: persistedState.localStorage, paths: ["cartId"] }]
});

const combineQuantityOfBasketItems = (
	guestBasketItems: BasketItem[],
	userBasketItems: BasketItem[] | undefined,
	prop: "ProductId"
) =>
	Object.values(
		[...guestBasketItems, ...(userBasketItems ? userBasketItems : [])].reduce(
			(
				acc: {
					[key: number]: BasketItem;
				},
				v
			) => {
				if (v[prop]) {
					if (acc[v[prop]!]) {
						v.Quantity += acc[v[prop]!]!.Quantity;
					}
					acc[v[prop]!] = acc[v[prop]!] ? { ...acc[v[prop]!], ...v } : { ...v };
				}
				return acc;
			},
			{}
		)
	);
